// @ts-nocheck
// CAMINHO: src/pages/AdministrativeProcessDetailPage.tsx
// VERSÃO: 1.0 - PÁGINA DE DETALHES PARA PROCESSOS ADMINISTRATIVOS

import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { 
  ArrowLeft, 
  Edit, 
  Users, 
  Scale, 
  Calendar, 
  FileText, 
  Building2, 
  Clock,
  DollarSign,
  CheckCircle,
  AlertCircle,
  User,
  Phone,
  Mail,
  MapPin,
  Plus,
  Trash2,
  ExternalLink,
  Archive,
  Loader2
} from 'lucide-react';
import { EditAdministrativeProcessModal } from '@/components/cases/EditAdministrativeProcessModal';
import { CaseFinancialSettings } from '@/components/cases/CaseFinancialSettings';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';

interface AdministrativeProcess {
    id: string;
    title: string;
    description?: string;
    status: string;
    priority: string;
    created_at: string;
    updated_at?: string;
    deadline?: string;
    responsible_lawyer?: string;
    client_id?: string;
    estimated_value?: number;
    actual_cost?: number;
    payment_status?: string;
    // Campos financeiros
    client_fees?: string;
    court_costs?: string;
    success_fee_type?: string;
    success_fee_value?: string;
    procedural_strategy?: string;
    internal_notes?: string;
    procedure_type?: string;
    administrative_agency?: string;
    process_number?: string;
    fee_configuration?: any;
    clients?: { 
        id: string;
        name: string; 
        email: string;
        phone?: string;
        address?: string;
    };
}

interface FinancialEntry {
    id: string;
    description: string;
    amount: number;
    entry_type: string;
    status: string;
    payment_date?: string;
    created_at: string;
}

interface TimelineEvent {
    id: string;
    title: string;
    description?: string;
    date: string;
    type: 'created' | 'updated' | 'payment' | 'deadline' | 'completed' | 'other';
    status: 'pending' | 'completed' | 'canceled';
    created_at: string;
}

export const AdministrativeProcessDetailPage = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { toast } = useToast();
    const [processDetails, setProcessDetails] = useState<AdministrativeProcess | null>(null);
    const [financialEntries, setFinancialEntries] = useState<FinancialEntry[]>([]);
    const [timeline, setTimeline] = useState<TimelineEvent[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('overview');
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isArchiveOpen, setIsArchiveOpen] = useState(false);
    const [archiveReason, setArchiveReason] = useState('');
    const [isArchiving, setIsArchiving] = useState(false);

    const handleArchive = async () => {
        if (!id) return;
        setIsArchiving(true);
        try {
            const { error } = await (supabase as any)
                .from('administrative_processes')
                .update({ archived: true, status: 'arquivado', archive_reason: archiveReason.trim() || null, archive_date: new Date().toISOString() })
                .eq('id', id);
            if (error) throw error;
            toast({ title: 'Processo arquivado', description: 'Ele agora está na aba Arquivados.' });
            setIsArchiveOpen(false);
            navigate('/archived-cases');
        } catch (error: any) {
            toast({ title: 'Não foi possível arquivar', description: error?.message, variant: 'destructive' });
        } finally {
            setIsArchiving(false);
        }
    };
    const [newEvent, setNewEvent] = useState({
        title: '',
        description: '',
        date: '',
        type: 'other' as TimelineEvent['type'],
        status: 'pending' as TimelineEvent['status']
    });
    const [editingEvent, setEditingEvent] = useState<string | null>(null);

    const fetchData = useCallback(async () => {
        if (!id) return;

        setIsLoading(true);
        try {
            // Buscar dados do processo administrativo
            const { data: processData, error: processError } = await supabase
                .from('administrative_processes')
                .select(`
                    *,
                    fee_configuration,
                    clients!administrative_processes_client_id_fkey(id, name, email, phone, address)
                `)
                .eq('id', id)
                .single();

            console.log('Dados brutos do processo:', processData);

            if (processError) throw processError;

            console.log('Dados do processo administrativo carregados: ', processData);
            setProcessDetails(processData);

            // Buscar entradas financeiras relacionadas
            const { data: financialData, error: financialError } = await supabase
                .from('financial_entries')
                .select('*')
                .ilike('description', `%${id}%`)
                .order('created_at', { ascending: false });

            if (financialError) {
                console.error('Erro ao carregar entradas financeiras:', financialError);
            } else {
                setFinancialEntries(financialData || []);
            }

            // Criar timeline básica
            const timelineEvents: TimelineEvent[] = [
                {
                    id: 'created',
                    title: 'Processo Criado',
                    description: 'Processo administrativo foi criado no sistema',
                    date: processData.created_at,
                    type: 'other',
                    status: 'completed',
                    created_at: processData.created_at
                },
                {
                    id: 'deadline-sample',
                    title: 'Prazo para Resposta',
                    description: 'Aguardando resposta do órgão competente',
                    date: processData.deadline || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
                    type: 'deadline',
                    status: 'pending',
                    created_at: processData.created_at
                }
            ];

            // Adicionar eventos financeiros à timeline
            if (financialData) {
                financialData.forEach(entry => {
                    timelineEvents.push({
                        id: `financial_${entry.id}`,
                        title: `${entry.entry_type === 'receita' ? 'Receita' : 'Despesa'} Registrada`,
                        description: `${entry.description} - R$ ${entry.amount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`,
                        date: entry.created_at,
                        type: 'payment',
                        status: entry.status === 'Pago' ? 'completed' : 'pending',
                        created_at: entry.created_at
                    });
                });
            }

            // Ordenar timeline por data
            timelineEvents.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
            setTimeline(timelineEvents);

        } catch (error) {
            console.error('Erro ao carregar dados:', error);
            toast({
                title: "Erro",
                description: "Não foi possível carregar os dados do processo.",
                variant: "destructive"
            });
        } finally {
            setIsLoading(false);
        }
    }, [id, toast]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const updatePaymentStatus = async (entryId: string, newStatus: string) => {
        try {
            const { error } = await supabase
                .from('financial_entries')
                .update({ status: newStatus })
                .eq('id', entryId);

            if (error) throw error;

            toast({
                title: "Sucesso",
                description: "Status de pagamento atualizado com sucesso."
            });

            // Adicionar evento à timeline quando pagamento for confirmado
            if (newStatus === 'Pago') {
                const entry = financialEntries.find(e => e.id === entryId);
                if (entry) {
                    const paymentEvent: TimelineEvent = {
                        id: `payment-${Date.now()}`,
                        title: `Pagamento Confirmado - ${entry.description}`,
                        description: `Valor: R$ ${entry.amount.toFixed(2)}`,
                        date: new Date().toISOString(),
                        type: 'payment',
                        status: 'completed',
                        created_at: new Date().toISOString()
                    };
                    setTimeline(prev => [...prev, paymentEvent].sort((a, b) => 
                        new Date(b.date).getTime() - new Date(a.date).getTime()
                    ));
                }
            }

            // Recarregar dados
            fetchData();
        } catch (error) {
            console.error('Erro ao atualizar status:', error);
            toast({
                title: "Erro",
                description: "Não foi possível atualizar o status de pagamento.",
                variant: "destructive"
            });
        }
    };

    const eventTypes = [
        { value: 'hearing', label: 'Audiência', color: 'bg-primary' },
        { value: 'deadline', label: 'Prazo', color: 'bg-yellow-500' },
        { value: 'document', label: 'Documento', color: 'bg-blue-500' },
        { value: 'petition', label: 'Petição', color: 'bg-purple-500' },
        { value: 'decision', label: 'Decisão', color: 'bg-indigo-500' },
        { value: 'payment', label: 'Pagamento', color: 'bg-green-500' },
        { value: 'other', label: 'Outros', color: 'bg-gray-500' }
    ];

    const statusOptions = [
        { value: 'pending', label: 'Pendente', color: 'text-yellow-600' },
        { value: 'completed', label: 'Concluído', color: 'text-green-600' },
        { value: 'canceled', label: 'Cancelado', color: 'text-red-600' }
    ];

    const addEvent = () => {
        if (!newEvent.title.trim() || !newEvent.date) return;

        const event: TimelineEvent = {
            id: Date.now().toString(),
            ...newEvent,
            created_at: new Date().toISOString()
        };

        const updatedTimeline = [...timeline, event].sort((a, b) => 
            new Date(b.date).getTime() - new Date(a.date).getTime()
        );

        setTimeline(updatedTimeline);
        setNewEvent({
            title: '',
            description: '',
            date: '',
            type: 'other',
            status: 'pending'
        });

        toast({
            title: "Sucesso",
            description: "Evento adicionado à timeline com sucesso."
        });
    };

    const updateEvent = (id: string, updates: Partial<TimelineEvent>) => {
        const updatedTimeline = timeline.map(event => 
            event.id === id ? { ...event, ...updates } : event
        ).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
        
        setTimeline(updatedTimeline);
        setEditingEvent(null);

        toast({
            title: "Sucesso",
            description: "Evento atualizado com sucesso."
        });
    };

    const removeEvent = (id: string) => {
        setTimeline(timeline.filter(e => e.id !== id));
        toast({
            title: "Sucesso",
            description: "Evento removido da timeline."
        });
    };

    const getTypeInfo = (type: string) => {
        return eventTypes.find(t => t.value === type) || eventTypes[eventTypes.length - 1];
    };

    const getStatusInfo = (status: string) => {
        return statusOptions.find(s => s.value === status) || statusOptions[0];
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                    <p className="text-muted-foreground">Carregando processo administrativo...</p>
                </div>
            </div>
        );
    }

    if (!processDetails) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <h2 className="text-xl font-semibold mb-2">Processo não encontrado</h2>
                    <p className="text-muted-foreground mb-4">O processo administrativo solicitado não foi encontrado.</p>
                    <Button onClick={() => navigate('/administrative-processes')}>
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Voltar para Processos Administrativos
                    </Button>
                </div>
            </div>
        );
    }

    const getStatusColor = (status: string) => {
        switch (status.toLowerCase()) {
            case 'concluído':
            case 'finalizado':
                return 'bg-green-100 text-green-800';
            case 'em andamento':
            case 'ativo':
                return 'bg-blue-100 text-blue-800';
            case 'pendente':
                return 'bg-yellow-100 text-yellow-800';
            case 'cancelado':
            case 'arquivado':
                return 'bg-red-100 text-red-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    const getPriorityColor = (priority: string) => {
        switch (priority.toLowerCase()) {
            case 'alta':
            case 'crítica':
                return 'bg-red-100 text-red-800';
            case 'média':
                return 'bg-yellow-100 text-yellow-800';
            case 'baixa':
                return 'bg-green-100 text-green-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    const totalReceitas = financialEntries
        .filter(entry => entry.entry_type === 'receita')
        .reduce((sum, entry) => sum + entry.amount, 0);

    const totalDespesas = financialEntries
        .filter(entry => entry.entry_type === 'despesa')
        .reduce((sum, entry) => sum + entry.amount, 0);

    const saldoLiquido = totalReceitas - totalDespesas;

    return (
        <div className="container mx-auto p-6 space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => navigate('/cases')}
                    >
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Voltar
                    </Button>
                    <div>
                        <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-blue-600">Processo administrativo</p>
                        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">{processDetails.title}</h1>
                        <p className="text-sm text-slate-500">
                            {[processDetails.procedure_type, processDetails.administrative_agency || processDetails.agency, processDetails.process_number || processDetails.administrative_number].filter(Boolean).join(' · ') || 'Sem número de protocolo'}
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <Badge className={getStatusColor(processDetails.status)}>
                        {processDetails.status}
                    </Badge>
                    <Badge className={getPriorityColor(processDetails.priority)}>
                        Prioridade {processDetails.priority}
                    </Badge>
                    {processDetails.archived ? (
                        <Badge className="bg-slate-100 text-slate-600">Arquivado</Badge>
                    ) : (
                        <Button
                            size="sm"
                            onClick={() => setIsArchiveOpen(true)}
                            className="h-9 rounded-xl border border-orange-200 bg-orange-50/60 text-[13px] text-orange-700 shadow-none hover:bg-orange-50"
                        >
                            <Archive className="w-4 h-4 mr-2" />
                            Arquivar processo
                        </Button>
                    )}
                    <Button size="sm" onClick={() => setIsEditModalOpen(true)} className="h-9 rounded-xl bg-[#c4ab83] text-[13px] font-semibold text-slate-900 hover:bg-[#b39a72]">
                         <Edit className="w-4 h-4 mr-2" />
                         Editar informações
                     </Button>
                </div>
            </div>

            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="grid w-full grid-cols-4">
                    <TabsTrigger value="overview">Visão Geral</TabsTrigger>
                    <TabsTrigger value="financial">Financeiro</TabsTrigger>
                    <TabsTrigger value="timeline">Timeline</TabsTrigger>
                    <TabsTrigger value="documents">Documentos</TabsTrigger>
                </TabsList>

                {/* Visão Geral */}
                <TabsContent value="overview" className="space-y-6">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {/* Informações Principais */}
                        <div className="lg:col-span-2 space-y-6">
                            <Card>
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                        <FileText className="w-5 h-5" />
                                        Informações do Processo
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <div>
                                        <label className="text-sm font-medium text-muted-foreground">Descrição</label>
                                        <p className="mt-1">{processDetails.description || 'Nenhuma descrição fornecida'}</p>
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="text-sm font-medium text-muted-foreground">Data de Criação</label>
                                            <p className="mt-1">
                                                {format(parseISO(processDetails.created_at), 'dd/MM/yyyy HH:mm', { locale: ptBR })}
                                            </p>
                                        </div>
                                        {processDetails.deadline && (
                                            <div>
                                                <label className="text-sm font-medium text-muted-foreground">Prazo</label>
                                                <p className="mt-1">
                                                    {format(parseISO(processDetails.deadline), 'dd/MM/yyyy', { locale: ptBR })}
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                    {processDetails.responsible_lawyer && (
                                        <div>
                                            <label className="text-sm font-medium text-muted-foreground">Advogado Responsável</label>
                                            <p className="mt-1">{processDetails.responsible_lawyer}</p>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>

                            {/* Valores Financeiros do Processo */}
                            <Card>
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                        <DollarSign className="w-5 h-5" />
                                        Valores Configurados
                                    </CardTitle>
                                    <CardDescription>
                                        Valores financeiros definidos para este processo
                                    </CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {(processDetails.client_fees || processDetails.fee_configuration?.initialFeeAmount) && (
                                            <div>
                                                <label className="text-sm font-medium text-muted-foreground">Honorários do Cliente</label>
                                                <p className="mt-1 text-lg font-semibold text-green-600">
                                                    R$ {processDetails.fee_configuration?.initialFeeAmount ? 
                                                        processDetails.fee_configuration.initialFeeAmount.toLocaleString('pt-BR', { minimumFractionDigits: 2 }) :
                                                        (parseFloat(processDetails.client_fees || '0')).toLocaleString('pt-BR', { minimumFractionDigits: 2 })
                                                    }
                                                </p>
                                            </div>
                                        )}
                                        {processDetails.court_costs && (
                                            <div>
                                                <label className="text-sm font-medium text-muted-foreground">Custas Processuais</label>
                                                <p className="mt-1 text-lg font-semibold text-orange-600">
                                                    R$ {(parseFloat(processDetails.court_costs || '0')).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                                </p>
                                            </div>
                                        )}
                                        {(processDetails.success_fee_value || processDetails.fee_configuration?.successFeeValue) && (
                                            <div>
                                                <label className="text-sm font-medium text-muted-foreground">Honorário de Êxito</label>
                                                <p className="mt-1 text-lg font-semibold text-blue-600">
                                                    {processDetails.fee_configuration?.successFeeType?.includes('percentage') || processDetails.success_fee_type === 'percentage' ? 
                                                        `${processDetails.fee_configuration?.successFeeValue || processDetails.success_fee_value}%` :
                                                        `R$ ${(parseFloat(processDetails.fee_configuration?.successFeeValue?.toString() || processDetails.success_fee_value || '0')).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
                                                    }
                                                </p>
                                            </div>
                                        )}
                                        {processDetails.success_fee_type && (
                                            <div>
                                                <label className="text-sm font-medium text-muted-foreground">Tipo de Honorário de Êxito</label>
                                                <p className="mt-1">
                                                    {processDetails.success_fee_type === 'percentage' ? 'Porcentagem' :
                                                     processDetails.success_fee_type === 'fixed' ? 'Valor Fixo' : 'Condicional'}
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                    {(!processDetails.client_fees && !processDetails.court_costs && !processDetails.success_fee_value && !processDetails.fee_configuration) && (
                                        <div className="text-center py-4">
                                            <p className="text-muted-foreground">Nenhum valor financeiro configurado para este processo.</p>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>

                            {/* Resumo Financeiro */}
                            <Card>
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                        <DollarSign className="w-5 h-5" />
                                        Resumo Financeiro
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="space-y-4">
                                        <div className="grid grid-cols-3 gap-4">
                                            <div className="text-center p-4 bg-green-50 rounded-lg">
                                                <p className="text-sm text-muted-foreground">Total Receitas</p>
                                                <p className="text-2xl font-bold text-green-600">
                                                    R$ {totalReceitas.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                                </p>
                                            </div>
                                            <div className="text-center p-4 bg-red-50 rounded-lg">
                                                <p className="text-sm text-muted-foreground">Total Despesas</p>
                                                <p className="text-2xl font-bold text-red-600">
                                                    R$ {totalDespesas.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                                </p>
                                            </div>
                                            <div className="text-center p-4 bg-blue-50 rounded-lg">
                                                <p className="text-sm text-muted-foreground">Saldo Líquido</p>
                                                <p className={`text-2xl font-bold ${
                                                    saldoLiquido >= 0 ? 'text-green-600' : 'text-red-600'
                                                }`}>
                                                    R$ {saldoLiquido.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex justify-end">
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => navigate('/financeiro')}
                                                className="flex items-center gap-2"
                                            >
                                                <ExternalLink className="w-4 h-4" />
                                                Ver Página Financeira
                                            </Button>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>

                        {/* Sidebar */}
                        <div className="space-y-6">
                            {/* Informações do Cliente */}
                            {processDetails.clients && (
                                <Card>
                                    <CardHeader>
                                        <CardTitle className="flex items-center gap-2">
                                            <User className="w-5 h-5" />
                                            Cliente
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent className="space-y-3">
                                        <div>
                                            <p className="font-medium">{processDetails.clients.name}</p>
                                        </div>
                                        {processDetails.clients.email && (
                                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                                <Mail className="w-4 h-4" />
                                                <span>{processDetails.clients.email}</span>
                                            </div>
                                        )}
                                        {processDetails.clients.phone && (
                                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                                <Phone className="w-4 h-4" />
                                                <span>{processDetails.clients.phone}</span>
                                            </div>
                                        )}
                                        {processDetails.clients.address && (
                                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                                <MapPin className="w-4 h-4" />
                                                <span>{processDetails.clients.address}</span>
                                            </div>
                                        )}
                                    </CardContent>
                                </Card>
                            )}

                            {/* Estatísticas */}
                            <Card>
                                <CardHeader>
                                    <CardTitle>Estatísticas</CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-3">
                                    <div className="flex justify-between">
                                        <span className="text-sm text-muted-foreground">Entradas Financeiras</span>
                                        <span className="font-medium">{financialEntries.length}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-sm text-muted-foreground">Pagamentos Pendentes</span>
                                        <span className="font-medium">
                                            {financialEntries.filter(e => e.status === 'Pendente').length}
                                        </span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-sm text-muted-foreground">Pagamentos Concluídos</span>
                                        <span className="font-medium">
                                            {financialEntries.filter(e => e.status === 'Pago').length}
                                        </span>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    </div>
                </TabsContent>

                {/* Aba Financeiro: mesmo formulário simples do processo judicial */}
                <TabsContent value="financial" className="space-y-6">
                    <CaseFinancialSettings key={processDetails.id} kind="administrative" caseDetails={processDetails} onSaved={fetchData} />
                </TabsContent>

                {/* Aba Timeline */}
                <TabsContent value="timeline" className="space-y-6">
                    {/* Formulário para adicionar evento */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Plus className="w-5 h-5" />
                                Adicionar Evento
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                <div>
                                    <Label htmlFor="event-title">Título</Label>
                                    <Input
                                        id="event-title"
                                        value={newEvent.title}
                                        onChange={(e) => setNewEvent(prev => ({ ...prev, title: e.target.value }))}
                                        placeholder="Título do evento"
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="event-date">Data</Label>
                                    <Input
                                        id="event-date"
                                        type="datetime-local"
                                        value={newEvent.date}
                                        onChange={(e) => setNewEvent(prev => ({ ...prev, date: e.target.value }))}
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="event-type">Tipo</Label>
                                    <Select value={newEvent.type} onValueChange={(value) => setNewEvent(prev => ({ ...prev, type: value as TimelineEvent['type'] }))}>
                                        <SelectTrigger>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {eventTypes.map(type => (
                                                <SelectItem key={type.value} value={type.value}>
                                                    {type.label}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label htmlFor="event-status">Status</Label>
                                    <Select value={newEvent.status} onValueChange={(value) => setNewEvent(prev => ({ ...prev, status: value as TimelineEvent['status'] }))}>
                                        <SelectTrigger>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {statusOptions.map(status => (
                                                <SelectItem key={status.value} value={status.value}>
                                                    {status.label}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <div className="mt-4">
                                <Label htmlFor="event-description">Descrição</Label>
                                <Textarea
                                    id="event-description"
                                    value={newEvent.description}
                                    onChange={(e) => setNewEvent(prev => ({ ...prev, description: e.target.value }))}
                                    placeholder="Descrição do evento (opcional)"
                                    rows={2}
                                />
                            </div>
                            <div className="mt-4 flex justify-end">
                                <Button onClick={addEvent} disabled={!newEvent.title.trim() || !newEvent.date}>
                                    <Plus className="w-4 h-4 mr-2" />
                                    Adicionar Evento
                                </Button>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Timeline */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Clock className="w-5 h-5" />
                                Timeline do Processo
                            </CardTitle>
                            <CardDescription>
                                Histórico de eventos e atividades relacionadas ao processo
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            {timeline.length === 0 ? (
                                <div className="text-center py-8">
                                    <Clock className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                                    <p className="text-muted-foreground">Nenhum evento registrado ainda.</p>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    {timeline.map((event, index) => {
                                        const typeInfo = getTypeInfo(event.type);
                                        const statusInfo = getStatusInfo(event.status);
                                        
                                        return (
                                            <div key={event.id} className="flex gap-4 group">
                                                <div className="flex flex-col items-center">
                                                    <div className={`w-4 h-4 rounded-full flex items-center justify-center ${
                                                        event.status === 'completed' ? 'bg-green-500' :
                                                        event.status === 'pending' ? 'bg-yellow-500' :
                                                        'bg-red-500'
                                                    }`}>
                                                        <div className="w-2 h-2 bg-white rounded-full" />
                                                    </div>
                                                    {index < timeline.length - 1 && (
                                                        <div className="w-px h-12 bg-border mt-2" />
                                                    )}
                                                </div>
                                                <div className="flex-1 pb-4">
                                                    <div className="flex items-center justify-between mb-2">
                                                        <div className="flex items-center gap-2">
                                                            <h4 className="font-medium">{event.title}</h4>
                                                            <Badge variant="outline" className={`text-xs ${typeInfo.color} text-white`}>
                                                                {typeInfo.label}
                                                            </Badge>
                                                            <Badge variant="outline" className={`text-xs ${statusInfo.color}`}>
                                                                {statusInfo.label}
                                                            </Badge>
                                                        </div>
                                                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                            <Button
                                                                size="sm"
                                                                variant="ghost"
                                                                onClick={() => setEditingEvent(event.id)}
                                                                className="h-6 w-6 p-0"
                                                            >
                                                                <Edit className="w-3 h-3" />
                                                            </Button>
                                                            <Button
                                                                size="sm"
                                                                variant="ghost"
                                                                onClick={() => removeEvent(event.id)}
                                                                className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                                                            >
                                                                <Trash2 className="w-3 h-3" />
                                                            </Button>
                                                        </div>
                                                    </div>
                                                    {event.description && (
                                                        <p className="text-sm text-muted-foreground mb-2">{event.description}</p>
                                                    )}
                                                    <p className="text-xs text-muted-foreground">
                                                        {format(parseISO(event.date), 'dd/MM/yyyy HH:mm', { locale: ptBR })}
                                                    </p>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Aba Documentos */}
                <TabsContent value="documents" className="space-y-6">
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <FileText className="w-5 h-5" />
                                Documentos
                            </CardTitle>
                            <CardDescription>
                                Documentos relacionados ao processo administrativo
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="text-center py-8">
                                <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                                <p className="text-muted-foreground mb-4">Funcionalidade de documentos será implementada em breve.</p>
                                <Button variant="outline" disabled>
                                    <FileText className="w-4 h-4 mr-2" />
                                    Adicionar Documento
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>
            
            {/* Modal de Edição */}
            <Dialog open={isArchiveOpen} onOpenChange={(open) => !isArchiving && setIsArchiveOpen(open)}>
                <DialogContent className="max-w-md rounded-2xl border-slate-200">
                    <DialogHeader className="text-left">
                        <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-orange-50">
                            <Archive className="h-4 w-4 text-orange-600" />
                        </div>
                        <DialogTitle className="text-lg font-semibold text-slate-900">Arquivar este processo?</DialogTitle>
                        <DialogDescription className="text-sm text-slate-500">
                            Ele sai da lista de processos ativos e vai para a aba Arquivados. Dá para desarquivar quando quiser.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-2">
                        <label htmlFor="archive_reason" className="block text-sm font-medium text-slate-700">Motivo (opcional)</label>
                        <Textarea
                            id="archive_reason"
                            value={archiveReason}
                            onChange={(e) => setArchiveReason(e.target.value)}
                            placeholder="Ex.: benefício concedido e honorários recebidos"
                            rows={2}
                            className="resize-none rounded-lg border-slate-200 text-sm shadow-none focus-visible:ring-[#c4ab83]/30"
                        />
                    </div>
                    <div className="flex justify-end gap-2 pt-2">
                        <Button variant="ghost" onClick={() => setIsArchiveOpen(false)} disabled={isArchiving} className="rounded-xl text-slate-600">Cancelar</Button>
                        <Button onClick={handleArchive} disabled={isArchiving} className="rounded-xl bg-slate-900 text-white hover:bg-slate-800">
                            {isArchiving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Archive className="mr-2 h-4 w-4 text-[#e8d5a8]" />}
                            Arquivar
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            <EditAdministrativeProcessModal
                processDetails={processDetails}
                isOpen={isEditModalOpen}
                onOpenChange={setIsEditModalOpen}
                onSuccess={fetchData}
            />
        </div>
    );
};

export default AdministrativeProcessDetailPage;